/**
 * Compress and resize image before upload
 * @param {File} file - Original image file
 * @param {number} maxWidth - Maximum width (default 1280px)
 * @param {number} maxHeight - Maximum height (default 1280px)
 * @param {number} quality - JPEG quality 0-1 (default 0.8)
 * @returns {Promise<File>} - Compressed image file
 */
export const compressImage = (file, maxWidth = 1280, maxHeight = 1280, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      reject(new Error('File is not an image'));
      return;
    }

    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let width = img.width;
        let height = img.height;
        
        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }
        
        // Create canvas and draw resized image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert canvas to blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              // Force .jpg name since output is always JPEG
              const baseName = (file.name || 'photo').replace(/\.[^.]+$/, '') || 'photo';
              const compressedFile = new File([blob], `${baseName}.jpg`, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              
              // Log compression results
              const originalSize = (file.size / 1024 / 1024).toFixed(2);
              const compressedSize = (compressedFile.size / 1024 / 1024).toFixed(2);
              const reduction = (((file.size - compressedFile.size) / file.size) * 100).toFixed(1);
              
              console.log(`Image compressed: ${originalSize}MB → ${compressedSize}MB (${reduction}% reduction)`);
              console.log(`Dimensions: ${img.width}x${img.height} → ${width}x${height}`);
              
              resolve(compressedFile);
            } else {
              reject(new Error('Canvas to Blob conversion failed'));
            }
          },
          'image/jpeg',
          quality
        );
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
      
      img.src = e.target.result;
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsDataURL(file);
  });
};

/**
 * Compress an image, falling back to the original file if compression fails
 * (e.g., formats the browser can't decode).
 */
export const compressImageSafe = async (file, maxWidth = 512, maxHeight = 512, quality = 0.85) => {
  try {
    return await compressImage(file, maxWidth, maxHeight, quality);
  } catch (err) {
    console.warn('Image compression failed, uploading original file:', err);
    return file;
  }
};

/** Build a user-friendly message for a failed profile picture upload. */
export const uploadErrorMessage = (error) => {
  if (error?.response?.status === 413) {
    return 'Image is too large — please choose a smaller photo';
  }
  return error?.response?.data?.detail || 'Failed to upload profile picture';
};
